// src/i18n.js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import Cookies from "js-cookie";
import { initializeLanguageSync, setLanguageCookie, broadcastLanguageChange, listenForLanguageChanges } from "./utils/languageSync";

// Import translation files
import translationEN from "./locales/en/translation.json";
import translationAR from "./locales/ar/translation.json";

// Translation resources
const resources = {
  en: { translation: translationEN },
  ar: { translation: translationAR },
};

// Initialize language from shared cookie or localStorage (same source for both apps)
const initialLanguage = initializeLanguageSync() || 'en';

// Apply lang + dir to <html> SYNCHRONOUSLY at module load so the first
// paint is in the correct direction. Without this, Arabic pages briefly
// flash in LTR before i18n.init().then() fires.
if (typeof document !== 'undefined' && document.documentElement) {
  const html = document.documentElement;
  html.setAttribute('lang', initialLanguage.startsWith('ar') ? 'ar' : 'en');
  html.setAttribute('dir', initialLanguage.startsWith('ar') ? 'rtl' : 'ltr');
}

// Initialize i18n — default English; do not use browser navigator (OS locale)
// so first-time visitors get English unless they have a saved cookie/localStorage.
i18n
  .use(initReactI18next)
  .use(HttpApi)
  .use(LanguageDetector)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar'],
    lng: initialLanguage,
    resources,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['cookie', 'localStorage', 'sessionStorage', 'htmlTag'],
      lookupCookie: 'shared_app_language',
      lookupLocalStorage: 'shared_app_language',
      caches: ['cookie', 'localStorage'],
    },
  })
  .then(() => {
    // Set initial language and direction
    const currentLang = i18n.language || initialLanguage;
    updateDirection(currentLang);
    
    // Listen for language changes from other apps/windows
    listenForLanguageChanges((newLanguage) => {
      if (i18n.language !== newLanguage) {
        i18n.changeLanguage(newLanguage);
        updateDirection(newLanguage);
      }
    });
  });

// Function to update the direction based on language
function updateDirection(language) {
  const htmlElement = document.documentElement;

  if (language.startsWith("ar")) {
    htmlElement.setAttribute("dir", "rtl"); // Right-to-left for Arabic
  } else {
    htmlElement.setAttribute("dir", "ltr"); // Left-to-right for other languages
  }
}

// Function to change language and update direction
export function changeLanguage(language) {
  i18n.changeLanguage(language); // Notify i18next of the language change
  updateDirection(language); // Adjust direction
  
  // Sync language to both cookie and localStorage (same source for both apps)
  setLanguageCookie(language);
  try {
    localStorage.setItem('shared_app_language', language);
    localStorage.setItem('selectedLanguage', language); // For backward compatibility
    localStorage.setItem('app_language', language); // Align with useLanguage / languageStorage
  } catch (error) {
    console.warn('Failed to save language to localStorage:', error);
  }
  broadcastLanguageChange(language);
}

export default i18n;
